import { ComponentIds, HierarchyUtils, ProductUtils, Ray, RaycastUtils, Transform, ValidationResultFail } from '@inter-ikea-core/core';
import { SystemBase } from '@inter-ikea-core/core-toolbox';
import { CLOTH_RAIL_ALIGN_SERIES_LEFT, CLOTH_RAIL_LEFT_FRAME_CONNECTOR_KEY, CLOTH_RAIL_OUTSIDE_CONNECTOR_KEY } from '../types';
import AdjustableComponent from '../components/AdjustableComponent';
import AdjustableHostComponent from '../components/AdjustableHostComponent';
import AdjustableClothrailLimitsComponent from '../components/AdjustableClothrailLimitsComponent';
import RequireSizeAdjustmentComponent from '../components/RequireSizeAdjustmentComponent';
/**
 * System that adapts the size of adjustable components to the size of the frame to the right. Both for the frame
 * itself and for the frame to the left of the frame. After the frame has been moved to a valid snap position.
 */
export class FrameDragAdaptAdjustableClothRailSystem extends SystemBase {
    constructor(coreApi) {
        super(ComponentIds.MOVED_TO_VALID_SNAP_POSITION_COMPONENT_ID);
        this.doingChangesOnComponent = [];
        this.systemsBeforeMe = null;
        this.systemsAfterMe = null;
        this.id = 'FrameDragAdaptAdjustableClothRailSystem';
        this.validateFrame = (_entity) => {
            // Expand the cloth rail on the right side of the frame to the frame to the right
            this.expandClothRailToExternalFrame(_entity);
            const getFrameToTheLeftResult = getFrameToTheLeft(this.coreApi, _entity);
            if (getFrameToTheLeftResult) {
                // Expand the cloth rail on the outside of frame on the left side of the frame towards this frame.
                this.expandClothRailToExternalFrame(getFrameToTheLeftResult.frameToTheLeft);
            }
        };
        this.getHostConnectorNumber = (hostConnector) => {
            return hostConnector
                .closestSubConnector(hostConnector.transform.position)
                ?.key.split('-')
                .pop();
        };
        /**
         * Expand the cloth rails on the outside of the frame to the right
         * @param entity Entity to expand the cloth rails for
         */
        this.expandClothRailToExternalFrame = (entity) => {
            if (isFrameWithClothRailToTheRight(entity)) {
                const clothRailsOnTheOutside = HierarchyUtils.children(entity).filter(e => e.hasComponent(AdjustableComponent.id));
                if (clothRailsOnTheOutside) {
                    clothRailsOnTheOutside.forEach(clothRailEntity => {
                        if (isOutsideClothRail(clothRailEntity)) {
                            const outerHit = measureClothRailToTheRight(this.coreApi, clothRailEntity);
                            const entityHit = outerHit?.entity;
                            if (!entityHit)
                                return null;
                            const outerHitEntity = entityHit?.hasComponent(ComponentIds.FRAME_COMPONENT_ID)
                                ? entityHit
                                : HierarchyUtils.findWithComponentInUpwardsHierarchy(entityHit, ComponentIds.FRAME_COMPONENT_ID);
                            if (outerHitEntity) {
                                const hostPointSeriesConnectorNumber = getHostSubConnectorNumber(clothRailEntity);
                                addRequiredSizeAdjustmentComponent(clothRailEntity, outerHitEntity, CLOTH_RAIL_ALIGN_SERIES_LEFT + hostPointSeriesConnectorNumber);
                            }
                        }
                    });
                }
            }
        };
        this.coreApi = coreApi;
    }
    onAddComponent(_entity, _newComponent) {
        super.onAddComponent(_entity, _newComponent);
        if (_entity.hasComponent(AdjustableHostComponent.id)) {
            const allFramesInStack = [_entity, ...this.coreApi.entities.getStackedEntities(_entity)];
            allFramesInStack.forEach(frame => {
                this.validateFrame(frame);
            });
        }
    }
}
/**
 * Check if the entity is a frame with a cloth rail to the right
 * @param entity Entity to check
 * @returns True if the entity is a frame with a cloth rail to the right
 */
export const isFrameWithClothRailToTheRight = (entity) => {
    const hostConnectorHasClothRail = entity
        ?.getComponent(ComponentIds.HOST_CONNECTIONS_COMPONENT_ID)
        ?.connections.some(c => c.hostConnector.key.includes(CLOTH_RAIL_OUTSIDE_CONNECTOR_KEY));
    const clothRailConnectorEnabled = entity
        .getComponent(ComponentIds.CONNECTORS_COMPONENT_ID)
        ?.connectors?.some(c => c.key === CLOTH_RAIL_OUTSIDE_CONNECTOR_KEY.slice(0, -1) // Remove the last character (-)
    );
    return (entity.hasComponent(ComponentIds.FRAME_COMPONENT_ID) &&
        (hostConnectorHasClothRail || clothRailConnectorEnabled));
};
/**
 * Check if the entity is a cloth rail that is outside the frame
 * @param entity Entity to check
 * @returns True if the entity is a cloth rail that is outside the frame
 */
export const isOutsideClothRail = (entity) => {
    return entity
        .getComponent(ComponentIds.GUEST_CONNECTIONS_COMPONENT_ID)
        ?.connections.some(c => c.hostConnector.key.includes(CLOTH_RAIL_OUTSIDE_CONNECTOR_KEY));
};
/**
 * Evaluate the frame with a cloth rail to the right and add a RequireSizeAdjustmentComponent if needed
 * to adjust its size to the frame to the right
 * @param coreApi CoreApi instance
 * @param frame Frame Entity
 * @returns ValidationResult if the distance is invalid or null if the distance is valid
 */
export const evaluateFrameWithClothRailToTheRight = (coreApi, frame, ignoreDistance = false) => {
    const frameClothRails = HierarchyUtils.children(frame).filter(entity => entity.hasComponent(AdjustableComponent.id));
    for (let i = 0; i < frameClothRails.length; i++) {
        const clothRailEntity = frameClothRails[i];
        const adjustableClothRailLimitsComponent = clothRailEntity.getComponent(AdjustableClothrailLimitsComponent.id);
        // We only care about cloth rails that are outside the frame
        if (isOutsideClothRail(clothRailEntity)) {
            const outerHit = measureClothRailToTheRight(coreApi, clothRailEntity);
            const entityHit = outerHit?.entity;
            if (entityHit
                ?.getComponent(ComponentIds.PRODUCT_COMPONENT_ID)
                ?.productRef.includes('right')) {
                return new ValidationResultFail('Must connect to a left panel', ProductUtils.productRefOrEmptyString(frame));
            }
            if (!entityHit || entityHit?.hasComponent(ComponentIds.ROOM_PART_COMPONENT_ID)) {
                return new ValidationResultFail('Must connect to a frame', ProductUtils.productRefOrEmptyString(frame));
            }
            const outerHitEntity = entityHit?.hasComponent(ComponentIds.FRAME_COMPONENT_ID)
                ? entityHit
                : HierarchyUtils.findWithComponentInUpwardsHierarchy(entityHit, ComponentIds.FRAME_COMPONENT_ID);
            // If we do not hit anything we do not allow the placement
            if (ignoreDistance || !outerHit) {
                return new ValidationResultFail('Invalid distance for product', ProductUtils.productRefOrEmptyString(frame));
            }
            if (outerHitEntity && adjustableClothRailLimitsComponent) {
                if (outerHitEntity.hasComponent(AdjustableHostComponent.id)) {
                    if (!ignoreDistance &&
                        !hasCorrectDistanceToNextFrame(outerHit.distance, clothRailEntity)) {
                        return new ValidationResultFail('Invalid distance for product', ProductUtils.productRefOrEmptyString(frame));
                    }
                }
            }
        }
    }
    return null;
};
/**
 * Get the sub connector number for the connector on the frame that the cloth rail is connected to
 * @param frame Host frame
 * @param clothesRail Guest cloth rail
 * @returns Number of the sub connector
 */
export const getHostSubConnectorNumber = (clothesRail) => {
    const clothRailConnectorKey = clothesRail.getComponent(ComponentIds.GUEST_CONNECTIONS_COMPONENT_ID)?.connections[0].hostConnector.key;
    const clothesRailPosition = getClothRailMeasurePosition(clothesRail);
    if (clothesRailPosition) {
        return clothRailConnectorKey ? clothRailConnectorKey.split('-').pop() : '';
    }
};
/**
 * Add a RequireSizeAdjustmentComponent to the cloth rail entity to make it adjust its size
 * @param clothRailEntity The cloth rail entity
 * @param targetEntity The target frame
 * @param hostConnectorNumber The host connector number on the target frame
 */
export const addRequiredSizeAdjustmentComponent = (clothRailEntity, targetEntity, hostConnectorKey) => {
    const pointSeriesConnector = targetEntity
        .getComponent(ComponentIds.CONNECTORS_COMPONENT_ID)
        ?.getConnectorByKey(hostConnectorKey);
    if (pointSeriesConnector) {
        clothRailEntity.addComponent(new RequireSizeAdjustmentComponent(targetEntity, pointSeriesConnector));
    }
};
/**
 * Check if the distance to the next frame is within the correct range
 * @param distance The distance to the next frame
 * @returns True if the distance is within the correct range
 */
export const hasCorrectDistanceToNextFrame = (distance, clothRailEntity) => {
    const compareDistance = Math.round(distance);
    const adjustableClothRailLimitsComponent = clothRailEntity.getComponent(AdjustableClothrailLimitsComponent.id);
    const correctDistance = adjustableClothRailLimitsComponent
        ? compareDistance >= adjustableClothRailLimitsComponent.min - 10 && // Allow a small margin since we have alignment connectors that will snap frame tom exact min and max values
            compareDistance <= adjustableClothRailLimitsComponent.max + 10
        : false;
    return correctDistance;
};
/**
 * Measure the distance from the cloth rail to the right to the next frame
 * @param coreApi CoreApi instance
 * @param clothRailEntity The cloth rail entity
 * @returns RaycastHit with the distance to the next frame
 */
export const measureClothRailToTheRight = (coreApi, clothRailEntity) => {
    const scene = coreApi.entities.getScene();
    if (scene) {
        const clothRailMeasurePosition = getClothRailMeasurePosition(clothRailEntity);
        if (clothRailMeasurePosition) {
            const measurePosition = clothRailMeasurePosition.position.plus(clothRailMeasurePosition.right.times(25));
            const ray = new Ray(measurePosition, clothRailMeasurePosition.forward.unaryMinus());
            return RaycastUtils.raycast(ray, scene, null);
        }
    }
};
/**
 * Get the position of the measure point for the cloth rail
 * @param clothRailEntity The cloth rail entity
 * @returns The position of the measure point
 */
export const getClothRailMeasurePosition = (clothRailEntity) => {
    const transformComponent = clothRailEntity.getComponent(ComponentIds.WORLD_TRANSFORM_COMPONENT_ID);
    if (transformComponent) {
        const clothRailFrameConnectorPosition = clothRailEntity
            .getComponent(ComponentIds.CONNECTORS_COMPONENT_ID)
            ?.getConnectorByKey(CLOTH_RAIL_LEFT_FRAME_CONNECTOR_KEY)
            ?.getWorldPositionFromOwnerEntity(clothRailEntity);
        if (!clothRailFrameConnectorPosition) {
            return null;
        }
        const measurePointTransform = new Transform(clothRailFrameConnectorPosition, transformComponent.transform.rotation);
        // Move measure point so we do not measure from inside of the frame
        const alteredMeasurePoint = measurePointTransform.position.minus(measurePointTransform.forward.times(25));
        return new Transform(alteredMeasurePoint, transformComponent.transform.rotation);
    }
    return null;
};
/**
 * Get the frame to the left of the current frame
 * @param coreApi CoreApi instance
 * @param frame The current frame
 * @returns The frame to the left of the current frame
 */
export const getFrameToTheLeft = (coreApi, frame) => {
    const frameTransform = frame.getComponent(ComponentIds.WORLD_TRANSFORM_COMPONENT_ID)?.transform;
    if (!frameTransform)
        return null;
    const frameSize = frame.getComponent(ComponentIds.COMMUNICATION_SIZE_COMPONENT_ID)?.size;
    const scene = coreApi.entities.getScene();
    const measurePosition = frameTransform?.position.minus(frameTransform?.right.times(frameSize ? frameSize.width / 2 : 0));
    if (measurePosition && frameTransform && scene) {
        const ray = new Ray(measurePosition, frameTransform.right.unaryMinus());
        const outerHit = RaycastUtils.raycast(ray, scene, null);
        const entityHit = outerHit?.entity;
        if (!entityHit)
            return null;
        if (entityHit
            ?.getComponent(ComponentIds.PRODUCT_COMPONENT_ID)
            ?.productRef.includes('left')) {
            return null;
        }
        const outerHitEntity = entityHit?.hasComponent(ComponentIds.FRAME_COMPONENT_ID)
            ? entityHit
            : HierarchyUtils.findWithComponentInUpwardsHierarchy(entityHit, ComponentIds.FRAME_COMPONENT_ID);
        if (outerHitEntity) {
            return { frameToTheLeft: outerHitEntity, distance: outerHit.distance };
        }
    }
    return null;
};
//# sourceMappingURL=FrameDragAdaptAdjustableClothRailSystem.js.map